import React from 'react';
import { ButtonProps } from '../../types/components.d';

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  className = '',
  onClick,
  type = 'button',
  icon,
  iconPosition = 'left',
  iconOnly = false,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none transform hover:-translate-y-0.5 active:translate-y-0';
  
  const variants = {
    primary: 'bg-primary-lightGreen text-white hover:bg-primary-lightGreen/90 hover:shadow-lg focus:ring-primary-lightGreen disabled:bg-gray-200 disabled:text-gray-400',
    secondary: 'bg-primary-teaGreen text-primary-black hover:bg-primary-teaGreen/90 hover:shadow-lg focus:ring-primary-lightGreen disabled:bg-gray-200 disabled:text-gray-400',
    secondaryBlue: 'bg-primary-teaBlue text-primary-black hover:bg-primary-teaBlue/90 hover:shadow-lg focus:ring-primary-lightBlue disabled:bg-gray-200 disabled:text-gray-400',
    outline: 'border-2 border-primary-lightGreen text-primary-lightGreen bg-transparent hover:bg-primary-lightGreen hover:text-gray-800 hover:border-primary-teaGreen focus:ring-primary-lightGreen disabled:border-gray-200 disabled:text-gray-400 disabled:bg-gray-100',
    outlineBlue: 'border-2 border-primary-lightBlue text-primary-lightBlue bg-transparent hover:bg-primary-lightBlue hover:text-gray-800 hover:border-primary-teaBlue focus:ring-primary-lightBlue disabled:border-gray-200 disabled:text-gray-400 disabled:bg-gray-100',
    ghost: 'text-primary-lightGreen bg-transparent hover:bg-primary-teaGreen/50 hover:text-primary-black focus:ring-primary-lightGreen disabled:text-gray-400 disabled:bg-transparent',
    danger: 'bg-red-600 text-white hover:bg-red-700 hover:shadow-lg focus:ring-red-500 disabled:bg-gray-200 disabled:text-gray-400',
    success: 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg focus:ring-green-500 disabled:bg-gray-200 disabled:text-gray-400',
    warning: 'bg-yellow-500 text-white hover:bg-yellow-600 hover:shadow-lg focus:ring-yellow-400 disabled:bg-gray-200 disabled:text-gray-400',
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl',
  } as const;

  const sizesIconOnly = {
    sm: 'p-2 text-sm rounded-full',
    md: 'p-2.5 text-base rounded-full',
    lg: 'p-3 text-lg rounded-full',
    xl: 'p-3.5 text-xl rounded-full',
  } as const;

  const classes = `${baseClasses} ${variants[variant]} ${iconOnly ? sizesIconOnly[size] : sizes[size]} ${className}`;
  
  const renderIcon = () => {
    if (!icon) return null;
    
    const iconClasses = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
    const marginClasses = iconOnly ? '' : iconPosition === 'left' ? 'mr-2' : 'ml-2';
    
    return (
      <span className={`${iconClasses} ${marginClasses}`}>
        {icon}
      </span>
    );
  };

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      
      {!loading && iconPosition === 'left' && renderIcon()}
      {!iconOnly && children}
      {!loading && iconPosition === 'right' && renderIcon()}
    </button>
  );
};

export default Button;
